// src/services/orderService.js
import axios from 'axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { groupByZone, getZoneColor, normalizeZone, sameZone, hexToRgb } from '../components/cartZones';
import { findItemImage, loadIndex } from '../components/cartImages';

// ============================== API ORDINI ===============================

const getAllOrders = async (token) => {
  try {
    const response = await axios.get('/api/orders', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching orders:', error);
    return [];
  }
};

const createOrder = async (orderData, token) => {
  try {
    const response = await axios.post('/api/orders', orderData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error("Errore durante il salvataggio dell'ordine:", error);
    throw error;
  }
};

const deleteOrder = async (orderId, token) => {
  try {
    await axios.delete(`/api/orders/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    console.error("Errore durante la cancellazione dell'ordine:", error);
    throw error;
  }
};

const updateOrder = async (updatedOrder, token) => {
  try {
    const response = await axios.put(`/api/orders/${updatedOrder.id}`, updatedOrder, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error("Errore durante l'aggiornamento dell'ordine:", error);
    throw error;
  }
};

// ============================== EXPORT EXCEL =============================

const orderToExcel = (order) => {
  const data = order.items.map(item => ({
    Codice: item.code,
    Descrizione: item.description,
    Listino: `€${formatPrice(item.unitPrice)}`,
    Sconto: `${item.discount}%`,
    'Prezzo Netto': `€${formatPrice(item.unitPrice * (1 - item.discount / 100))}`,
    Quantità: item.quantity,
    'Prezzo Totale': `€${formatPrice(item.unitPrice * item.quantity * (1 - item.discount / 100))}`,
    Note: item.notes,
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, `Ordine_${order.orderNumber}`);
  XLSX.writeFile(workbook, `ordine_${order.orderNumber}.xlsx`);
};

// ============================== URL/LOGO HELPERS =========================

// Inline urlAsset: normalizza verso https://www.goooled.it/...
const urlAsset = (n) => {
  n = String(n || '').split('goooled.it').pop();
  n = n.split('/').filter(e => (e ?? '').trim() !== '').join('/');
  return (new URL(n, 'https://www.goooled.it')).toString();
};

// Configura il path CDN per il logo (produzione)
const CDN_LOGO_PATH = '/images/logo.png';

// (DEV) imposta qui l’URL assoluto locale del logo con hash.
const LOCAL_LOGO_ABS_URL = 'http://localhost:5010/static/media/logo.7e9ebbe8216ab8e9132a.png'; // <-- aggiorna qui

// Scova un logo a runtime già presente nel DOM (utile in dev con hash dinamico)
function discoverRuntimeLogoUrl() {
  try {
    const imgs = document.querySelectorAll('img');
    for (const img of imgs) {
      const alt = (img.alt || '').toLowerCase();
      const src = String(img.src || '');
      if (/logo/.test(alt) || /\/static\/media\/.*logo/i.test(src) || /logo/i.test(src)) {
        return src;
      }
    }
  } catch { /* no DOM */ }
  return null;
}

// URL (HTTP/S) -> DataURL PNG (jsPDF richiede DataURL) con gestione same-origin/CORS + fallback <img>+canvas
async function imgUrlToPngDataUrl(url) {
  let mode;
  try {
    const u = new URL(url, window.location.href);
    mode = (u.origin === window.location.origin) ? undefined : 'cors';
  } catch {
    mode = 'cors';
  }

  try {
    console.debug('[logo] fetch ->', url, 'mode:', mode ?? '(default)');
    const res = await fetch(url, { mode, cache: 'force-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();

    if (!blob.type || !blob.type.startsWith('image/')) {
      throw new Error(`Bad MIME: ${blob.type || 'unknown'}`);
    }

    const dataUrl = await new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
    console.debug('[logo] fetch ok -> dataURL length:', (dataUrl?.length || 0));
    return dataUrl;
  } catch (e) {
    console.warn('[logo] fetch->dataURL failed for', url, 'err:', e?.message || e);
    // Fallback <img> + canvas
    try {
      console.debug('[logo] fallback <img>+canvas ->', url);
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => resolve(im);
        im.onerror = () => reject(new Error('img onerror'));
        im.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');
      console.debug('[logo] fallback ok -> dataURL length:', (dataUrl?.length || 0));
      return dataUrl;
    } catch (e2) {
      console.warn('[logo] fallback failed for', url, 'err:', e2?.message || e2);
      return null;
    }
  }
}

// Cache in-memory del DataURL del logo
let __logoDataUrl = null;
let __logoDataUrlPromise = null;

// Strategia: preferisci URL passato in opts, poi DOM runtime (dev), poi LOCAL_LOGO_ABS_URL, poi CDN.
async function getLogoDataUrl(preferredUrl) {
  if (__logoDataUrl) {
    console.debug('[logo] cached dataURL already available.');
    return __logoDataUrl;
  }
  if (__logoDataUrlPromise) {
    console.debug('[logo] using pending promise for dataURL.');
    return __logoDataUrlPromise;
  }

  const isLocalhost = typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.host);

  const candidates = [
    // Logo dal B2B stesso (/api/products/image): /images/logo.png online viene rediretto a www.goooled.it,
    // che non permette di leggere l'immagine (CORS) e il PDF restava senza logo.
    '/api/products/image?p=' + encodeURIComponent('images/logo.png'),
    preferredUrl || null,
    isLocalhost ? discoverRuntimeLogoUrl() : null,
    isLocalhost ? LOCAL_LOGO_ABS_URL : null,
    urlAsset(CDN_LOGO_PATH),
  ].filter(Boolean);

  console.debug('[logo] candidates:', candidates);

  __logoDataUrlPromise = (async () => {
    let lastErr = null;
    for (const u of candidates) {
      console.debug('[logo] try candidate:', u);
      try {
        const dataUrl = await imgUrlToPngDataUrl(u);
        if (dataUrl) {
          __logoDataUrl = dataUrl;
          console.debug('[logo] using:', u);
          return dataUrl;
        }
      } catch (e) {
        lastErr = e;
        console.warn('[logo] candidate failed:', u, 'err:', e?.message || e);
      }
    }
    console.warn('[logo] nessun logo disponibile.', lastErr ? `Ultimo errore: ${lastErr?.message || lastErr}` : '');
    __logoDataUrl = null;
    return null;
  })().finally(() => { __logoDataUrlPromise = null; });

  return __logoDataUrlPromise;
}
function buildDatasheetUrl(dsRaw) {
  if (!dsRaw) return null;
  let ds = String(dsRaw).trim();

  if (/^https?:\/\//i.test(ds)) {
    return ds;
  }

  // Se NON inizia con "datasheet/", lo prependo
  if (!/^datasheet\//i.test(ds)) {
    ds = `datasheet/${ds}`;
  }

  return urlAsset(ds);
}

function getCatalogDatasheetOverride(item) {
  const code = String(item?.code || item?.codice || '').trim().toUpperCase();
  const description = String(item?.description || '').trim().toUpperCase();

  if (
    /^3134#96\b/.test(code) ||
    /^GL3134#9[23]\b/.test(code) ||
    description.includes('GEMINI 96') ||
    description.includes('GEMINI 2X96') ||
    description.includes('GEMINI 3X96')
  ) {
    return 'datasheet//3134XX.pdf';
  }

  return null;
}

function buildItemDatasheetUrl(item) {
  if (!item) return null;

  if (item.datasheetConfig && item.datasheetType === 'profileDynamic') {
    try {
      const json = JSON.stringify(item.datasheetConfig);
      const b64 = btoa(json); // base64 semplice

      // forziamo direttamente il dominio pubblico
      const origin = 'https://b2b.goooled.com';

      return `${origin}/api/products/datasheet/profile-link?c=${encodeURIComponent(
        b64
      )}`;
    } catch (e) {
      console.error(
        '[orderToPdf] errore buildItemDatasheetUrl dinamico:',
        e
      );
    }
  }

  const overrideDatasheet = getCatalogDatasheetOverride(item);
  if (overrideDatasheet) return buildDatasheetUrl(overrideDatasheet);

  return buildDatasheetUrl(item.datasheet);
}



// ============================== PDF PREVENTIVO ==========================

export function orderToPdf(order, user, opts = {}) {
  // PDF preventivo/ordine — redesign 2026-10 (mockup "Preventivo PDF goooled"):
  // intestazione con logo e numero in evidenza, Da/Per, riferimento e note, righe per zona con foto,
  // riepilogo, condizioni commerciali, coordinate bancarie e accettazione. Stessi dati di prima.
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const extractVatCountryCode = (vatValue) => {
    const raw = String(vatValue || '').trim().toUpperCase();
    if (!raw) return '';
    const compact = raw.replace(/[^A-Z0-9]/g, '');
    const maybePrefix = compact.slice(0, 2);
    return /^[A-Z]{2}$/.test(maybePrefix) ? maybePrefix : '';
  };

  const isForeignBusinessCustomer = () => {
    const recipient = order?.recipient || {};
    const hasCompany = !!String(recipient.company || user?.companyName || '').trim();
    if (!hasCompany) return false;
    const vatCountry = extractVatCountryCode(recipient.vat);
    if (vatCountry) return vatCountry !== 'IT';
    const fallbackCountry = String(order?.customerCountry || user?.country || '').trim().toUpperCase();
    return !!fallbackCountry && fallbackCountry !== 'IT';
  };

  const baseVatRate = typeof opts.vatRate === 'number' ? opts.vatRate : (order.vatRate ?? 22);
  const effectiveVatRate = isForeignBusinessCustomer() ? 0 : baseVatRate;

  // --- Config -------------------------------------------------------------
  const cfg = {
    company: {
      name: opts.companyName || 'Goooled srl',
      address: opts.companyAddress || 'Via Torricelli 26, 33080 Porcia (PN) Italia',
      vat: opts.companyVat || 'P. IVA 01717240939',
      email: opts.companyEmail || 'info@goooled.com',
      phone: opts.companyPhone || '+39 0434 317912',
      website: opts.companyWebsite || 'www.goooled.com',
      sdi: opts.companySdi || 'b2b.goooled.com',
      pec: opts.companyPec || '',
    },
    logoDataUrl: opts.logoDataUrl || null, // DataURL PNG
    vatRate: effectiveVatRate,
    showWatermark: !!opts.watermark,
    watermark: opts.watermark || '',
    margins: { top: 14, right: 15, bottom: 16, left: 15 },
  };

  // Palette del mockup (stessa del nuovo carrello)
  const C = {
    ink: [14, 21, 33],
    muted: [90, 104, 120],
    line: [160, 172, 184],   // più scuro: deve vedersi anche stampato
    soft: [236, 241, 245],   // fondo riquadro "Per" e foto mancanti
    accent: [0, 153, 190],
    danger: [180, 35, 24],
  };
  const setText = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const setFill = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const setDraw = (c) => doc.setDrawColor(c[0], c[1], c[2]);
  const LINE_W = 0.3; // spessore standard di linee e riquadri (mm), leggibile in stampa
  doc.setLineWidth(LINE_W);

  const page = { w: doc.internal.pageSize.getWidth(), h: doc.internal.pageSize.getHeight() };
  const L = cfg.margins.left;
  const R = page.w - cfg.margins.right;
  const contentW = R - L;
  const HEADER_H = 40;          // spazio riservato all'intestazione su ogni pagina
  const FOOTER_Y = page.h - 10; // riga del piè di pagina

  // --- Helpers ------------------------------------------------------------
  const fmtCurrency = (v) => {
    const val = Number(v || 0);
    return `€ ${val.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const fmtPct = (v) => `${(Number(v || 0)).toLocaleString('it-IT', { maximumFractionDigits: 2 })}%`;
  const fmtDateIT = (d) => {
    try {
      const date = (d instanceof Date) ? d : new Date(d);
      return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return ''; }
  };
  const cleanText = (text) => String(text ?? '')
    .replace(/<\s*br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\r\n|\r|\n|\t/g, ' ')
    .replace(/[ ​‌‍﻿]/g, ' ')
    .replace(/[^,]*:\s*undefined\s*(,|$)/gi, '') // campi vuoti nelle descrizioni vecchie
    .replace(/\s*,\s*,/g, ',')
    .replace(/,\s*$/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  const cap = (label, x, y) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.4);
    setText(C.muted);
    doc.text(String(label).toUpperCase(), x, y, { charSpace: 0.35 });
  };

  const title = (order.type || 'Preventivo');

  // --- Intestazione (ridisegnata su ogni pagina alla fine, con il totale pagine) ---
  const drawHeader = (pageNumber, totalPages) => {
    // Logo (o nome azienda se manca)
    const logoH = 9;
    const ratio = Number(opts.logoRatio ?? 2.9);
    if (cfg.logoDataUrl) {
      try {
        doc.addImage(cfg.logoDataUrl, 'PNG', L, 11, logoH * ratio, logoH, undefined, 'FAST');
      } catch { /* senza logo si prosegue */ }
    } else {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      setText(C.ink);
      doc.text(cfg.company.name, L, 18);
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.8);
    setText(C.muted);
    doc.text(`${cfg.company.name} · ${cfg.company.address}`, L, 25.5);
    doc.text(
      [cfg.company.vat, cfg.company.email, cfg.company.phone, cfg.company.website].filter(Boolean).join(' · '),
      L, 29
    );

    // Tipo documento + numero in evidenza + data / pagina
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    setText(C.ink);
    doc.text(title, R, 17, { align: 'right' });
    doc.setFontSize(15);
    setText(C.accent);
    doc.text(`N° ${order.orderNumber ?? ''}`, R, 24.5, { align: 'right' });

    const meta = [
      ['Data', fmtDateIT(order.date)],
      ...(order.validUntil ? [['Validità', fmtDateIT(order.validUntil)]] : []),
      ['Pagina', `${pageNumber} di ${totalPages}`],
    ];
    let my = 29.5;
    doc.setFontSize(7.3);
    meta.forEach(([k, v]) => {
      doc.setFont('helvetica', 'bold');
      setText(C.ink);
      doc.text(String(v), R, my, { align: 'right' });
      const vw = doc.getTextWidth(String(v));
      doc.setFont('helvetica', 'normal');
      setText(C.muted);
      doc.text(k, R - vw - 3, my, { align: 'right' });
      my += 3.6;
    });

    // filo ciano
    setDraw(C.accent);
    doc.setLineWidth(0.7);
    doc.line(L, 37, R, 37);
    doc.setLineWidth(LINE_W);
  };

  // --- Piè di pagina ---
  const drawFooter = (pageNumber, totalPages) => {
    setDraw(C.line);
    doc.setLineWidth(LINE_W);
    doc.line(L, FOOTER_Y - 4, R, FOOTER_Y - 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.6);
    setText(C.muted);
    const cond = opts.footerConditions || 'Condizioni generali disponibili su richiesta. Prezzi oltre IVA. Offerta salvo verifica tecnica.';
    doc.text(cond, L, FOOTER_Y, { maxWidth: contentW - 45 });
    doc.text(`${title} N° ${order.orderNumber ?? ''} · pag. ${pageNumber}/${totalPages}`, R, FOOTER_Y, { align: 'right' });
  };

  if (cfg.showWatermark && cfg.watermark) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(60);
    doc.setTextColor(230);
    doc.text(cfg.watermark, page.w / 2, page.h / 2, { align: 'center', angle: 25 });
  }

  // --- Da / Per ---
  const drawParties = (y) => {
    const gap = 8;
    const colW = (contentW - gap) / 2;
    const leftX = L;
    const rightX = L + colW + gap;

    const agent = order?.agent || {};
    const leftLines = [
      agent.name ? `Agente / referente: ${agent.name}` : '',
      [agent.email, agent.phone].filter(Boolean).join(' · '),
      [cfg.company.vat, cfg.company.sdi || cfg.company.pec].filter(Boolean).join(' · '),
    ].filter(Boolean);

    const safeUser = user || {};
    const recipient = order?.recipient || {};
    const target = {
      name: recipient.name || safeUser.name || '',
      company: recipient.company || safeUser.company || safeUser.companyName || '',
      address: recipient.address || safeUser.address || '',
      email: recipient.email || safeUser.email || '',
      phone: recipient.phone || safeUser.phone || '',
      vat: recipient.vat || safeUser.vat || safeUser.fiscalCode || '',
    };
    const toTitle = target.company || target.name || '';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.6);
    const rightLines = [
      target.company && target.name && target.name.trim().toLowerCase() !== target.company.trim().toLowerCase() ? target.name : '',
      target.address,
      [target.vat ? `P. IVA / C.F. ${target.vat}` : '', target.email, target.phone].filter(Boolean).join(' · '),
    ].filter(Boolean).flatMap((l) => doc.splitTextToSize(l, colW - 8));

    const lineH = 3.7;
    const boxH = Math.max(22, 13 + rightLines.length * lineH + 3);

    // Da
    cap('Da', leftX, y + 4);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    setText(C.ink);
    doc.text(cfg.company.name, leftX, y + 10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.6);
    setText(C.muted);
    leftLines.forEach((l, i) => doc.text(l, leftX, y + 15 + i * lineH));

    // Per (riquadro chiaro)
    setFill(C.soft);
    doc.roundedRect(rightX, y, colW, boxH, 2, 2, 'F');
    cap('Per', rightX + 4, y + 5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    setText(C.ink);
    doc.text(doc.splitTextToSize(toTitle || '—', colW - 8)[0], rightX + 4, y + 10.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.6);
    setText(C.muted);
    rightLines.forEach((l, i) => doc.text(l, rightX + 4, y + 15.5 + i * lineH));

    return y + boxH + 5;
  };

  // --- Riferimento + Note generali ---
  const drawRefAndNotes = (y) => {
    const gap = 8;
    const colW = (contentW - gap) / 2;
    const refText = String(order.reference || '—').trim();
    const noteText = String(order.notes || '').trim();
    doc.setFontSize(8.2);
    const refLines = doc.splitTextToSize(refText, noteText ? colW : contentW);
    const noteLines = noteText ? doc.splitTextToSize(noteText, colW) : [];
    const h = 9 + Math.max(refLines.length, noteLines.length, 1) * 3.8 + 2;

    setDraw(C.line);
    doc.line(L, y, R, y);
    cap('Riferimento', L, y + 5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    setText(C.ink);
    doc.text(refLines, L, y + 9.5);
    if (noteText) {
      const nx = L + colW + gap;
      cap('Note generali', nx, y + 5);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.2);
      doc.text(noteLines, nx, y + 9.5);
    }
    doc.line(L, y + h, R, y + h);
    return y + h + 5;
  };

  // --- Tabella righe ---
  // Colonne (180 mm): # · foto · articolo (codice + descrizione + nota) · q.tà · listino · sc. · netto · totale
  const COL = { idx: 7, ph: 14, art: 78, qty: 10, list: 19, disc: 11, net: 19, tot: 22 };
  const ART_TEXT_W = COL.art - 3.2;
  const LINE_PITCH = 3.05; // mm per riga a 7,5 pt
  const head = [['#', '', 'Articolo', 'Q.tà', 'Listino', 'Sc.', 'Netto', 'Totale']];

  let imponibile = 0;
  const pdfItems = groupByZone(order.items || []);
  const zoneColorOf = getZoneColor(pdfItems);
  const zoneCount = {};
  pdfItems.forEach((it) => {
    const z = normalizeZone(it.zone).toLowerCase();
    if (z) zoneCount[z] = (zoneCount[z] || 0) + 1;
  });

  const hasZones = Object.keys(zoneCount).length > 0;
  const noZoneCount = pdfItems.filter((it) => !normalizeZone(it.zone)).length;
  const rowMeta = [];  // allineato al body: { type: 'zone'|'item', item, lines, kinds, zone }
  const body = [];
  pdfItems.forEach((it, i) => {
    const zone = normalizeZone(it.zone);
    const startsGroup = i === 0 || !sameZone(it, pdfItems[i - 1]);
    if (startsGroup && (zone || hasZones)) {
      body.push([{ content: '', colSpan: 8 }]);
      rowMeta.push({ type: 'zone', zone, count: zone ? zoneCount[zone.toLowerCase()] || 0 : noZoneCount });
    }
    const qty = Number(it.quantity || 0);
    const unit = Number(it.unitPrice || 0);
    const sc = Number(it.discount || 0);
    const netUnit = unit * (1 - sc / 100);
    const tot = netUnit * qty;
    imponibile += tot;

    // testo dell'articolo, già spezzato sulla larghezza della colonna
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    const descLines = doc.splitTextToSize(cleanText(it.description || ''), ART_TEXT_W);
    doc.setFontSize(6.8);
    const noteLines = it.notes ? doc.splitTextToSize(`Nota: ${cleanText(it.notes)}`, ART_TEXT_W) : [];
    const lines = [String(it.code || ''), ...descLines, ...noteLines];
    const kinds = ['code', ...descLines.map(() => 'desc'), ...noteLines.map(() => 'note')];

    body.push([
      String(body.filter((_, k) => rowMeta[k]?.type === 'item').length + 1),
      '',
      lines.join('\n'),
      String(qty),
      fmtCurrency(unit),
      fmtPct(sc),
      fmtCurrency(netUnit),
      fmtCurrency(tot),
    ]);
    rowMeta.push({ type: 'item', item: it, lines, kinds, zone, isAdj: it.type === 'adjustment' || unit < 0 });
  });

  const images = opts.itemImages; // Map item -> dataURL (preparata da orderToPdfWithLogo)

  let cursorY = HEADER_H + 2;
  cursorY = drawParties(cursorY);
  cursorY = drawRefAndNotes(cursorY);

  doc.autoTable({
    startY: cursorY,
    head,
    body,
    theme: 'plain',
    margin: { left: L, right: cfg.margins.right, top: HEADER_H + 2, bottom: 18 },
    rowPageBreak: 'avoid',
    styles: {
      font: 'helvetica',
      fontSize: 7.5,
      textColor: C.ink,
      cellPadding: { top: 1.8, right: 1.6, bottom: 1.8, left: 1.6 },
      valign: 'top',
      overflow: 'linebreak',
      lineHeightFactor: 1.15,
    },
    headStyles: {
      fontSize: 6.3,
      fontStyle: 'bold',
      textColor: C.muted,
      cellPadding: { top: 0.5, right: 1.6, bottom: 2, left: 1.6 },
    },
    columnStyles: {
      0: { cellWidth: COL.idx, halign: 'right', textColor: C.muted },
      1: { cellWidth: COL.ph },
      2: { cellWidth: COL.art },
      3: { cellWidth: COL.qty, halign: 'right' },
      4: { cellWidth: COL.list, halign: 'right' },
      5: { cellWidth: COL.disc, halign: 'right' },
      6: { cellWidth: COL.net, halign: 'right' },
      7: { cellWidth: COL.tot, halign: 'right', fontStyle: 'bold' },
    },

    didParseCell: (data) => {
      if (data.section === 'head') {
        if (data.column.index >= 3) data.cell.styles.halign = 'right';
        return;
      }
      const meta = rowMeta[data.row.index];
      if (!meta) return;
      if (meta.type === 'zone') {
        data.cell.styles.minCellHeight = 6.5;
        data.cell.styles.cellPadding = { top: 1.6, right: 2, bottom: 1.6, left: 3.5 };
        return;
      }
      data.cell.styles.minCellHeight = 14;
      if (meta.isAdj && (data.column.index === 2 || data.column.index === 7)) {
        data.cell.styles.textColor = C.danger;
      }
    },

    // L'articolo lo disegniamo noi (codice in mono grigio, descrizione, nota): qui si toglie il testo standard
    willDrawCell: (data) => {
      if (data.section !== 'body') return;
      const meta = rowMeta[data.row.index];
      if (meta?.type === 'zone' || (meta?.type === 'item' && data.column.index === 2)) {
        data.cell.text = [];
      }
    },

    didDrawCell: (data) => {
      const { cell } = data;
      if (data.section === 'head') {
        if (data.column.index === 0) {
          setDraw(C.ink);
          doc.setLineWidth(0.3);
          doc.line(L, cell.y + cell.height, R, cell.y + cell.height);
          doc.setLineWidth(LINE_W);
        }
        return;
      }
      const meta = rowMeta[data.row.index];
      if (!meta) return;

      // fascia della zona: nome + numero di righe, bordo colorato a sinistra
      if (meta.type === 'zone') {
        const color = meta.zone ? zoneColorOf(meta.zone) : { bg: '#F1F3F6', accent: '#8A95A3' };
        const [br, bg, bb] = hexToRgb(color.bg);
        const [ar, ag, ab] = hexToRgb(color.accent);
        doc.setFillColor(br, bg, bb);
        doc.rect(L, cell.y, contentW, cell.height, 'F');
        doc.setFillColor(ar, ag, ab);
        doc.rect(L, cell.y, 1.2, cell.height, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.2);
        setText(C.ink);
        const name = (meta.zone || 'Altri articoli').toUpperCase();
        doc.text(name, L + 3.5, cell.y + cell.height / 2 + 1.2, { charSpace: 0.3 });
        const nw = doc.getTextWidth(name) + name.length * 0.3;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.6);
        setText(C.muted);
        doc.text(`${meta.count} ${meta.count === 1 ? 'riga' : 'righe'}`, L + 3.5 + nw + 3, cell.y + cell.height / 2 + 1.2);
        return;
      }

      const it = meta.item;
      // separatore riga + bordo zona (prima colonna)
      if (data.column.index === 0) {
        setDraw(C.line);
        doc.line(L, cell.y + cell.height, R, cell.y + cell.height);
        if (meta.zone) {
          const [ar, ag, ab] = hexToRgb(zoneColorOf(meta.zone).accent);
          doc.setFillColor(ar, ag, ab);
          doc.rect(L, cell.y, 1.2, cell.height, 'F');
        }
      }

      // foto prodotto (o riquadro neutro)
      if (data.column.index === 1) {
        const size = 11;
        const x = cell.x + 1.4;
        const y = cell.y + 1.6;
        const img = images && images.get ? images.get(it) : null;
        if (img) {
          try {
            doc.addImage(img, 'JPEG', x, y, size, size, undefined, 'FAST');
          } catch { /* immagine non valida: solo il riquadro */ }
        } else {
          setFill(C.soft);
          doc.rect(x, y, size, size, 'F');
        }
        setDraw(C.line);
        doc.roundedRect(x, y, size, size, 1, 1, 'S');
      }

      // articolo: codice (mono, grigio) + link scheda, descrizione, nota
      if (data.column.index === 2) {
        const pad = 1.6;
        let y = cell.y + 1.8;
        meta.lines.forEach((line, i) => {
          const kind = meta.kinds[i];
          if (kind === 'code') {
            doc.setFont('courier', 'normal');
            doc.setFontSize(6.6);
            setText(C.muted);
          } else if (kind === 'note') {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6.8);
            setText(C.muted);
          } else {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);
            setText(meta.isAdj ? C.danger : C.ink);
          }
          // Profili su misura: la lunghezza del profilo (es. "300cm") in grassetto, più evidente
          const isProfile = it.datasheetType === 'profileDynamic' || !!it.datasheetConfig?.modelName;
          const lengthMatch = kind === 'desc' && isProfile && !meta.lengthDone ? /\b\d+(?:[.,]\d+)?\s?cm\b/.exec(line) : null;
          if (lengthMatch) {
            meta.lengthDone = true;
            const before = line.slice(0, lengthMatch.index);
            const after = line.slice(lengthMatch.index + lengthMatch[0].length);
            let x = cell.x + pad;
            doc.text(before, x, y, { baseline: 'top' });
            x += doc.getTextWidth(before);
            doc.setFont('helvetica', 'bold');
            doc.text(lengthMatch[0], x, y, { baseline: 'top' });
            x += doc.getTextWidth(lengthMatch[0]);
            doc.setFont('helvetica', 'normal');
            doc.text(after, x, y, { baseline: 'top' });
          } else {
            doc.text(line, cell.x + pad, y, { baseline: 'top' });
          }
          if (kind === 'code') {
            const linkUrl = buildItemDatasheetUrl(it);
            if (linkUrl) {
              const cw = doc.getTextWidth(line);
              const tx = cell.x + pad + cw + 2.2;
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(5.6);
              const label = 'Scheda';
              const lw = doc.getTextWidth(label) + 2.4;
              setDraw(C.accent);
              doc.roundedRect(tx, y - 0.3, lw, 3.1, 0.6, 0.6, 'S');
              setText(C.accent);
              doc.text(label, tx + 1.2, y + 0.2, { baseline: 'top' });
              doc.link(tx, y - 0.3, lw, 3.1, { url: linkUrl });
              // tutta la cella articolo resta cliccabile, come prima
              doc.link(cell.x, cell.y, cell.width, cell.height, { url: linkUrl });
            }
          }
          y += LINE_PITCH;
        });
      }
    },
  });

  // --- Riepilogo ---
  let y = doc.lastAutoTable.finalY + 6;
  const iva = imponibile * (cfg.vatRate / 100);
  const shipping = Number(order.shipping || 0);
  const totRows = [
    ['Imponibile', fmtCurrency(imponibile)],
    ['Spese', shipping ? fmtCurrency(shipping) : '—'],
    [`IVA ${fmtPct(cfg.vatRate)}`, fmtCurrency(iva)],
  ];
  const boxW = 72;
  const boxH = 8 + totRows.length * 5.2 + 13;
  if (y + boxH > FOOTER_Y - 8) {
    doc.addPage();
    y = HEADER_H + 4;
  }
  const bx = R - boxW;
  setDraw(C.line);
  doc.roundedRect(bx, y, boxW, boxH, 2, 2, 'S');
  let ty = y + 7;
  doc.setFontSize(8.2);
  totRows.forEach(([k, v]) => {
    doc.setFont('helvetica', 'normal');
    setText(C.muted);
    doc.text(k, bx + 4, ty);
    setText(C.ink);
    doc.text(v, R - 4, ty, { align: 'right' });
    ty += 5.2;
  });
  setDraw(C.ink);
  doc.setLineWidth(0.3);
  doc.line(bx + 4, ty - 2, R - 4, ty - 2);
  doc.setLineWidth(LINE_W);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setText(C.ink);
  doc.text('Totale', bx + 4, ty + 5);
  doc.setFontSize(15);
  doc.text(fmtCurrency(imponibile + shipping + iva), R - 4, ty + 5.5, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  setText(C.muted);
  doc.text('Prezzi in euro. Le schede tecniche si aprono dal riquadro "Scheda" accanto al codice.', L, y + boxH - 2, { maxWidth: contentW - boxW - 8 });
  y += boxH + 8;

  // --- Condizioni commerciali · Coordinate bancarie · Accettazione ---
  const payment = order?.payment || {};
  const paymentMethodMap = {
    bank_transfer: 'Bonifico bancario',
    riba_30: 'Ri.Ba. 30gg',
    riba_60: 'Ri.Ba. 60gg',
    riba_90: 'Ri.Ba. 90gg',
    cash: 'Contanti',
    card: 'Carta',
    other: 'Altro',
  };
  const commercialRows = [
    ['Pagamento', paymentMethodMap[payment.method] || payment.method || '-'],
    ['Condizioni', payment.terms || '-'],
    ['Scadenza', payment.dueDays != null && payment.dueDays !== '' ? `${payment.dueDays} giorni` : '-'],
    ['Acconto', payment.depositPercent != null && payment.depositPercent !== '' ? `${payment.depositPercent}%` : '-'],
    ['Consegna', payment.deliveryTime || '-'],
    ...(payment.notes ? [['Note', payment.notes]] : []),
  ];
  const blocksH = 50;
  const signH = 30;
  if (y + blocksH + signH + 6 > FOOTER_Y - 8) {
    doc.addPage();
    y = HEADER_H + 4;
  }
  const gap = 8;
  const colW = (contentW - gap) / 2;

  // Condizioni commerciali
  setDraw(C.line);
  doc.roundedRect(L, y, colW, blocksH, 2, 2, 'S');
  cap('Condizioni commerciali', L + 4, y + 6);
  let cy = y + 12;
  commercialRows.forEach(([k, v]) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    setText(C.muted);
    doc.text(k, L + 4, cy);
    setText(C.ink);
    const vLines = doc.splitTextToSize(String(v), colW - 30);
    doc.text(vLines[0], L + 26, cy);
    cy += 5.4;
  });

  // Coordinate bancarie
  const bxx = L + colW + gap;
  doc.roundedRect(bxx, y, colW, blocksH, 2, 2, 'S');
  cap('Coordinate bancarie', bxx + 4, y + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  setText(C.muted);
  doc.text('Banca', bxx + 4, y + 12);
  doc.text('Intestato a', bxx + 4, y + 17.4);
  setText(C.ink);
  doc.text('Banca Popolare Alto Adige', bxx + 26, y + 12);
  doc.text(cfg.company.name, bxx + 26, y + 17.4);
  cap('IBAN', bxx + 4, y + 25);
  doc.setFont('courier', 'bold');
  doc.setFontSize(10);
  setText(C.ink);
  doc.text('IT29 I058 5664 9901 0657 1292 238', bxx + 4, y + 31);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  setText(C.muted);
  doc.text('SWIFT', bxx + 4, y + 38);
  setText(C.ink);
  doc.text('BPAAIT2B106', bxx + 26, y + 38);

  // Accettazione del cliente
  const sy = y + blocksH + 6;
  setDraw(C.line);
  doc.roundedRect(L, sy, contentW, signH, 2, 2, 'S');
  cap('Accettazione del cliente', L + 4, sy + 6);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.8);
  setText(C.muted);
  doc.text(`Per accettazione del presente ${title.toLowerCase()} e delle condizioni indicate.`, L + 4, sy + 11.5);
  setDraw(C.ink);
  doc.line(L + 4, sy + signH - 7, L + 54, sy + signH - 7);
  doc.line(L + 64, sy + signH - 7, R - 4, sy + signH - 7);
  doc.setFontSize(7);
  doc.text('Data', L + 4, sy + signH - 3.2);
  doc.text('Timbro e firma', L + 64, sy + signH - 3.2);

  // --- Intestazione e piè di pagina su tutte le pagine, con il totale pagine ---
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawHeader(i, totalPages);
    drawFooter(i, totalPages);
  }

  const sanitizeFilenamePart = (s) =>
    String(s || '')
      .replace(/[\\/:*?"<>|]+/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const filenameRecipient = order?.recipient || {};
  const customerName = sanitizeFilenamePart(
    filenameRecipient.company || filenameRecipient.name || user?.company || user?.name || ''
  );

  const filename = `${(order.type || 'Preventivo')}_${order.orderNumber}${customerName ? `_${customerName}` : ''}.pdf`;
  if (opts.returnBlob) return doc.output('blob');
  doc.save(filename);
}

// Foto prodotto per il PDF: stessa logica del carrello (cartImages), lette tramite il B2B
// (/api/products/image) e ridotte a miniature JPEG per tenere leggero il file.
async function loadItemImagesForPdf(items) {
  const map = new Map();
  if (typeof document === 'undefined') return map;
  let index = null;
  try {
    index = await loadIndex();
  } catch {
    index = null;
  }
  const toThumb = (url) =>
    new Promise((resolve) => {
      const img = new Image();
      const timer = setTimeout(() => resolve(null), 8000);
      img.onload = () => {
        clearTimeout(timer);
        try {
          const size = 160;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, size, size);
          const scale = Math.min(size / img.naturalWidth, size / img.naturalHeight);
          const w = img.naturalWidth * scale;
          const h = img.naturalHeight * scale;
          ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.82));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => {
        clearTimeout(timer);
        resolve(null);
      };
      img.src = url;
    });
  await Promise.all(
    (items || []).map(async (item) => {
      const raw = findItemImage(item, index);
      if (!raw) return;
      const p = String(raw).split('goooled.it').pop().replace(/^\/+/, '');
      const thumb = await toThumb(`/api/products/image?p=${encodeURIComponent(p)}`);
      if (thumb) map.set(item, thumb);
    })
  );
  return map;
}

// Wrapper: SEMPRE con logo (e foto prodotto). Puoi passare un URL preferito (es. dal DB).
export async function orderToPdfWithLogo(order, user, opts = {}) {
  const [logoDataUrl, itemImages] = await Promise.all([
    getLogoDataUrl(opts.logoUrl),
    loadItemImagesForPdf(order?.items).catch(() => new Map()),
  ]);
  const finalOpts = { ...opts, logoDataUrl, itemImages };
  return orderToPdf(order, user, finalOpts);
}

// ============================== UTILS ====================================

const formatPrice = (price, digits = 2) => {
  return price.toFixed(digits).replace('.', ',');
};

export { getAllOrders, createOrder, deleteOrder, updateOrder, orderToExcel, formatPrice };
